import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DefaultButton,
  DefaultLoadingButton,
} from '../../../common/components/DefaultButton/DefaultButton'
import FormCheckbox from '../../../common/components/FormCheckbox/FormCheckbox'
import FormToggle from '../../../common/components/FormToggle/FormToggle'
import DatePicker from '../../../common/components/InputField/DatePicker'
import TextArea from '../../../common/components/InputField/TextArea'
import TextField from '../../../common/components/InputField/TextField'
import { ColumnFlex, MinMaxDiv } from '../../../common/components/Layout/Layout'
import ScrollToError from '../../../common/components/ScrollToError/ScrollToError'
import { Text } from '../../../common/components/Typography/Typography'
import { scale } from '../../../common/utils/scale'
import { createPohjantahtiFormValidationSchema } from '../utils/form'

import { ButtonContainer } from './FormComponents'

export type PohjantahtiInformation = {
  incidentDate?: Date
  incidentDescription: string
  previouslyTreated?: boolean
  previouslyClaimedInsurance?: boolean
  insuranceClaimId?: string
  informationProvidedBy: string
  mehilainenToPohjantahtiDisclosureConsent: boolean
  pohjantahtiToMehilainenDisclosureConsent: boolean
}

const StyledList = styled.ul`
  list-style: disc;
`

const HeaderContainer = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: ${scale(4)};

  h1 {
    margin-bottom: ${scale(1.5)};
  }

  ${StyledList} {
    margin-left: ${scale(2)};
  }
`

const Header: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()
  return (
    <HeaderContainer className={className}>
      <Text as="h1" $weight="Medium" $size={500} $height="Large" $center>
        {t('component.pohjantahtiForm.header.heading')}
      </Text>
      <Text as="p" $height="Large" $size={300}>
        {t('component.pohjantahtiForm.header.text')}
      </Text>
      <StyledList>
        {t('component.pohjantahtiForm.header.listItems')
          .split(';')
          .map((listItem, index) => (
            <li key={`pohjantahtiListItem-${index}`}>
              <Text $size={300}>{listItem}</Text>
            </li>
          ))}
        <li>
          <Text
            as="a"
            href={t('component.pohjantahtiForm.header.link')}
            target="_blank"
            $size={300}
          >
            {t('component.pohjantahtiForm.header.linkText')}
          </Text>
        </li>
      </StyledList>
    </HeaderContainer>
  )
}

const Container = styled(ColumnFlex)`
  margin: ${scale(1)};
  margin-top: ${scale(5)};
  margin-bottom: ${scale(5)};
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: ${scale(1.5)};
`

interface Props {
  onSubmit(data: PohjantahtiInformation): void
  onBack(): void
}

const PohjantahtiForm: React.FC<Props> = ({ onSubmit, onBack }) => {
  const { t } = useTranslation()

  const validationSchema = createPohjantahtiFormValidationSchema(t)

  const initialValues: PohjantahtiInformation = {
    incidentDescription: '',
    informationProvidedBy: '',
    mehilainenToPohjantahtiDisclosureConsent: false,
    pohjantahtiToMehilainenDisclosureConsent: false,
  }

  return (
    <MinMaxDiv size="740px">
      <Container>
        <Formik<typeof initialValues>
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={onSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <StyledForm>
              <ScrollToError />
              <Header />
              <DatePicker
                id="incidentDate"
                label={t('component.pohjantahtiForm.incidentDate')}
                helperText={touched.incidentDate && errors.incidentDate}
                error={Boolean(touched.incidentDate && errors.incidentDate)}
                value={values.incidentDate}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                data-cy="pohjantahtiIncidentDate"
                max={dayjs().format('YYYY-MM-DD')}
              />
              <TextArea
                id="incidentDescription"
                label={t('component.pohjantahtiForm.incidentDescription')}
                rows={4}
                helperText={touched.incidentDescription && errors.incidentDescription}
                error={Boolean(touched.incidentDescription && errors.incidentDescription)}
                value={values.incidentDescription}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                data-cy="pohjantahtiIncidentDescriptionField"
              />
              <FormToggle
                id="previouslyTreated"
                label={t('component.pohjantahtiForm.previouslyTreated')}
                onChange={handleChange}
                required
              />
              {values.previouslyTreated && (
                <FormToggle
                  id="previouslyClaimedInsurance"
                  label={t('component.pohjantahtiForm.previouslyClaimedInsurance')}
                  onChange={handleChange}
                  required
                />
              )}
              {values.previouslyClaimedInsurance && (
                <TextField
                  id="insuranceClaimId"
                  label={t('component.pohjantahtiForm.insuranceClaimId')}
                  placeholder="xxx-xxxxxxx"
                  helperText={touched.insuranceClaimId && errors.insuranceClaimId}
                  error={Boolean(touched.insuranceClaimId && errors.insuranceClaimId)}
                  value={values.insuranceClaimId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  data-cy="pohjantahtiInsuranceClaimIdField"
                />
              )}
              <TextField
                id="informationProvidedBy"
                label={t('component.pohjantahtiForm.informationProvidedBy')}
                helperText={touched.informationProvidedBy && errors.informationProvidedBy}
                error={Boolean(touched.informationProvidedBy && errors.informationProvidedBy)}
                value={values.informationProvidedBy}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                data-cy="pohjantahtiInformationProvidedByField"
              />
              <FormCheckbox
                id="mehilainenToPohjantahtiDisclosureConsent"
                checked={values.mehilainenToPohjantahtiDisclosureConsent}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('component.pohjantahtiForm.mehilainenToPohjantahtiDisclosureConsent')}
                description={t(
                  'component.pohjantahtiForm.mehilainenToPohjantahtiDisclosureConsentText'
                )}
                helperText={
                  touched.mehilainenToPohjantahtiDisclosureConsent &&
                  errors.mehilainenToPohjantahtiDisclosureConsent
                }
                error={Boolean(
                  touched.mehilainenToPohjantahtiDisclosureConsent &&
                    errors.mehilainenToPohjantahtiDisclosureConsent
                )}
                required
              />
              <FormCheckbox
                id="pohjantahtiToMehilainenDisclosureConsent"
                checked={values.pohjantahtiToMehilainenDisclosureConsent}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('component.pohjantahtiForm.pohjantahtiToMehilainenDisclosureConsent')}
                description={t(
                  'component.pohjantahtiForm.pohjantahtiToMehilainenDisclosureConsentText'
                )}
                helperText={
                  touched.pohjantahtiToMehilainenDisclosureConsent &&
                  errors.pohjantahtiToMehilainenDisclosureConsent
                }
                error={Boolean(
                  touched.pohjantahtiToMehilainenDisclosureConsent &&
                    errors.pohjantahtiToMehilainenDisclosureConsent
                )}
                required
              />
              <ButtonContainer>
                <DefaultLoadingButton
                  type="submit"
                  data-cy="pohjantahtiContinueButton"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {t('common.continue')}
                </DefaultLoadingButton>
                <DefaultButton type="button" variant="outlined" onClick={onBack}>
                  {t('common.back')}
                </DefaultButton>
              </ButtonContainer>
            </StyledForm>
          )}
        </Formik>
      </Container>
    </MinMaxDiv>
  )
}

export default PohjantahtiForm
